import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Math as MathFormula } from "@/components/Math";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function QuadraticEquationSolver() {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [c, setC] = useState("");
  const [result, setResult] = useState<{ x1: string; x2: string } | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Math", url: "https://calclab.app/math" },
      { name: "Quadratic Equation Solver", url: "https://calclab.app/calculator/quadratic-equation" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Quadratic Equation Solver",
      "Solve quadratic equations ax² + bx + c = 0 with step-by-step solutions. Free online quadratic formula calculator.",
      "https://calclab.app/calculator/quadratic-equation",
      lang
    )
  ]);

  const solve = () => {
    const aNum = parseFloat(a);
    const bNum = parseFloat(b);
    const cNum = parseFloat(c);

    if (isNaN(aNum) || isNaN(bNum) || isNaN(cNum)) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid numbers for all coefficients.",
        variant: "destructive",
      });
      return;
    }

    if (aNum === 0) {
      toast({
        title: "Invalid Equation",
        description: "Coefficient 'a' cannot be zero in a quadratic equation.",
        variant: "destructive",
      });
      return;
    }

    const discriminant = bNum * bNum - 4 * aNum * cNum;

    if (discriminant > 0) {
      const x1 = (-bNum + Math.sqrt(discriminant)) / (2 * aNum);
      const x2 = (-bNum - Math.sqrt(discriminant)) / (2 * aNum);
      setResult({ x1: x1.toFixed(4), x2: x2.toFixed(4) });
      toast({
        title: "Two Real Solutions",
        description: "The equation has two distinct real roots.",
      });
    } else if (discriminant === 0) {
      const x = -bNum / (2 * aNum);
      setResult({ x1: x.toFixed(4), x2: "Same as x₁" });
      toast({
        title: "One Real Solution",
        description: "The equation has one repeated root.",
      });
    } else {
      const realPart = (-bNum / (2 * aNum)).toFixed(4);
      const imaginaryPart = (Math.sqrt(-discriminant) / (2 * aNum)).toFixed(4);
      setResult({
        x1: `${realPart} + ${imaginaryPart}i`,
        x2: `${realPart} - ${imaginaryPart}i`,
      });
      toast({
        title: "Complex Solutions",
        description: "The equation has two complex roots.",
      });
    }
  };

  const reset = () => {
    setA("");
    setB("");
    setC("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Quadratic Equation Solver" 
      calculatorPath="/calculator/quadratic-equation"
      category="math"
      subcategory="Algebra"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Quadratic Equation Solver</CardTitle>
            <CardDescription>
              Solve equations of the form ax² + bx + c = 0
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="a">Coefficient a (x² term)</Label>
                <Input
                  id="a"
                  type="number"
                  step="any"
                  value={a}
                  onChange={(e) => setA(e.target.value)}
                  placeholder="Enter coefficient a"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="b">Coefficient b (x term)</Label>
                <Input
                  id="b"
                  type="number"
                  step="any"
                  value={b}
                  onChange={(e) => setB(e.target.value)}
                  placeholder="Enter coefficient b"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="c">Coefficient c (constant)</Label>
                <Input
                  id="c"
                  type="number"
                  step="any"
                  value={c}
                  onChange={(e) => setC(e.target.value)}
                  placeholder="Enter coefficient c"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={solve} className="flex-1">Solve</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <>
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Solutions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p>
                      <span className="font-semibold">x₁ = </span>
                      <span className="text-xl">{result.x1}</span>
                    </p>
                    <p>
                      <span className="font-semibold">x₂ = </span>
                      <span className="text-xl">{result.x2}</span>
                    </p>
                  </CardContent>
                </Card>

                <CalculatorSuperCalcCTA config={calculatorCTAs["quadratic"]} trigger={result?.x1} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Quadratic Formula</CardTitle>
              </CardHeader>
              <CardContent>
                <MathFormula block>{String.raw`x = \frac{-b \pm \sqrt{b^2 - 4ac}}{2a}`}</MathFormula>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
